import { FormEvent, useCallback, useEffect, useState } from "react";
import Navbar from "../../../components/Navbar";
import API from "../../../services/api";

type TransactionType = "credit" | "debit";

interface User {
  _id: string;
  full_name?: string;
  email?: string;
  phone_prefix?: string;
  phone_number?: string;
  wallet_balance?: number;
}

interface UsersResponse {
  users: User[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
  };
}

interface WalletResponse {
  message?: string;
  data?: {
    wallet_balance?: number;
    walletBalance?: number;
  };
  wallet_balance?: number;
  walletBalance?: number;
}

const USERS_PER_PAGE = 10;

export default function AdminWallet() {
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [totalUserPages, setTotalUserPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [userId, setUserId] = useState("");
  const [amount, setAmount] = useState("");
  const [type, setType] = useState<TransactionType>("credit");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [balance, setBalance] = useState<number | null>(null);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError("");

    try {
      const params: Record<string, string | number> = {
        page: userPage,
        limit: USERS_PER_PAGE,
      };

      if (search) {
        params.search = search;
      }

      const response = await API.get("/users", { params });
      const data: UsersResponse = response.data?.data || response.data;

      setUsers(Array.isArray(data?.users) ? data.users : []);
      setTotalUserPages(Math.max(1, data?.pagination?.totalPages || 1));
      setTotalUsers(data?.pagination?.totalItems || data?.users?.length || 0);
    } catch (requestError: any) {
      setUsers([]);
      setUsersError(
        requestError?.response?.data?.message || "Failed to load users."
      );
    } finally {
      setUsersLoading(false);
    }
  }, [search, userPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const selectUser = (user: User) => {
    setSelectedUser(user);
    setUserId(user._id);
    setError("");
    setSuccess("");
    setBalance(null);
  };

  const handleUserSearch = (event: FormEvent) => {
    event.preventDefault();
    setUserPage(1);
    setSearch(searchInput.trim());
  };

  const clearUserSearch = () => {
    setSearchInput("");
    setSearch("");
    setUserPage(1);
  };

  const validate = () => {
    if (!/^[a-f\d]{24}$/i.test(userId.trim())) {
      return "Select a user or enter a valid 24-character MongoDB user ID.";
    }

    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      return "Amount must be greater than ₹0.";
    }

    if (!description.trim()) {
      return "Please enter a description for the wallet adjustment.";
    }

    return "";
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setBalance(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const parsedAmount = Number(amount);
    const customerLabel =
      selectedUser?._id === userId.trim()
        ? selectedUser.full_name || selectedUser.phone_number || selectedUser._id
        : userId.trim();

    if (
      !window.confirm(
        `Confirm ${type} of ₹${parsedAmount.toFixed(2)} for ${customerLabel}?`
      )
    ) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await API.post<WalletResponse>("/user/wallet", {
        userId: userId.trim(),
        amount: parsedAmount,
        type,
        description: description.trim(),
      });

      const responseData = response.data;
      const updatedBalance =
        responseData?.data?.wallet_balance ??
        responseData?.data?.walletBalance ??
        responseData?.wallet_balance ??
        responseData?.walletBalance;

      setSuccess(
        responseData?.message ||
          `Wallet ${type === "credit" ? "credited" : "debited"} successfully.`
      );

      if (typeof updatedBalance === "number") {
        setBalance(updatedBalance);
        setUsers((currentUsers) =>
          currentUsers.map((user) =>
            user._id === userId.trim()
              ? { ...user, wallet_balance: updatedBalance }
              : user
          )
        );
        setSelectedUser((currentUser) =>
          currentUser?._id === userId.trim()
            ? { ...currentUser, wallet_balance: updatedBalance }
            : currentUser
        );
      } else {
        fetchUsers();
      }

      setAmount("");
      setDescription("");
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.response?.data?.error ||
          "Wallet adjustment failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main style={styles.page}>
        <div style={styles.pageContent}>
          <section style={styles.userCard}>
            <div style={styles.sectionHeader}>
              <div>
                <h1 style={styles.title}>Select Customer</h1>
                <p style={styles.subtitle}>
                  Customer list loaded from <strong>/api/users</strong>
                </p>
              </div>
              <span style={styles.totalBadge}>{totalUsers} users</span>
            </div>

            <form onSubmit={handleUserSearch} style={styles.searchRow}>
              <input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search by name, email, or phone..."
                style={styles.input}
              />
              <button type="submit" style={styles.searchButton}>
                Search
              </button>
              {(search || searchInput) && (
                <button
                  type="button"
                  onClick={clearUserSearch}
                  style={styles.clearButton}
                >
                  Clear
                </button>
              )}
            </form>

            {usersError && (
              <div role="alert" style={styles.errorAlert}>
                <span>{usersError}</span>
                <button
                  type="button"
                  onClick={fetchUsers}
                  style={styles.retryButton}
                >
                  Retry
                </button>
              </div>
            )}

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeadRow}>
                    <th style={styles.tableHeadCell}>Customer</th>
                    <th style={styles.tableHeadCell}>Phone</th>
                    <th style={styles.tableHeadCell}>Wallet</th>
                    <th style={styles.tableHeadCell}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {usersLoading ? (
                    <tr>
                      <td colSpan={4} style={styles.emptyCell}>
                        Loading customers...
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={styles.emptyCell}>
                        No customers found.
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => {
                      const isSelected = user._id === userId;

                      return (
                        <tr
                          key={user._id}
                          onClick={() => selectUser(user)}
                          style={{
                            ...styles.tableRow,
                            ...(isSelected ? styles.selectedRow : {}),
                          }}
                        >
                          <td style={styles.tableCell}>
                            <div style={styles.customerName}>
                              {user.full_name || "Unnamed customer"}
                            </div>
                            <div style={styles.customerEmail}>
                              {user.email || user._id}
                            </div>
                          </td>
                          <td style={styles.tableCell}>
                            {[user.phone_prefix, user.phone_number]
                              .filter(Boolean)
                              .join(" ") || "—"}
                          </td>
                          <td style={styles.tableCell}>
                            <strong style={styles.walletAmount}>
                              ₹{Number(user.wallet_balance || 0).toFixed(2)}
                            </strong>
                          </td>
                          <td style={styles.tableCell}>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                selectUser(user);
                              }}
                              style={{
                                ...styles.selectButton,
                                ...(isSelected
                                  ? styles.selectedButton
                                  : {}),
                              }}
                            >
                              {isSelected ? "Selected" : "Select"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div style={styles.pagination}>
              <button
                type="button"
                disabled={userPage <= 1 || usersLoading}
                onClick={() => setUserPage((current) => current - 1)}
                style={{
                  ...styles.pageButton,
                  ...(userPage <= 1 || usersLoading
                    ? styles.disabledButton
                    : {}),
                }}
              >
                ← Previous
              </button>
              <span style={styles.pageLabel}>
                Page {userPage} of {totalUserPages}
              </span>
              <button
                type="button"
                disabled={userPage >= totalUserPages || usersLoading}
                onClick={() => setUserPage((current) => current + 1)}
                style={{
                  ...styles.pageButton,
                  ...(userPage >= totalUserPages || usersLoading
                    ? styles.disabledButton
                    : {}),
                }}
              >
                Next →
              </button>
            </div>
          </section>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <div>
                <h2 style={styles.title}>Wallet Adjustment</h2>
                <p style={styles.subtitle}>
                  Credit a bonus/refund or debit a correction amount.
                </p>
              </div>
              <div
                style={{
                  ...styles.typeBadge,
                  ...(type === "credit"
                    ? styles.creditBadge
                    : styles.debitBadge),
                }}
              >
                {type === "credit" ? "Credit" : "Debit"}
              </div>
            </div>

            {selectedUser && selectedUser._id === userId && (
              <div style={styles.selectedCustomer}>
                <div>
                  <strong>
                    {selectedUser.full_name || "Unnamed customer"}
                  </strong>
                  <div style={styles.selectedCustomerDetail}>
                    {[selectedUser.phone_prefix, selectedUser.phone_number]
                      .filter(Boolean)
                      .join(" ") || selectedUser.email || selectedUser._id}
                  </div>
                </div>
                <div style={styles.selectedBalance}>
                  Current wallet: ₹
                  {Number(selectedUser.wallet_balance || 0).toFixed(2)}
                </div>
              </div>
            )}

            {error && (
              <div role="alert" style={styles.errorAlert}>
                {error}
              </div>
            )}

            {success && (
              <div role="status" style={styles.successAlert}>
                <div>{success}</div>
                {balance !== null && (
                  <strong>Updated wallet balance: ₹{balance.toFixed(2)}</strong>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} style={styles.form}>
              <label style={styles.field}>
                <span style={styles.label}>User ID</span>
                <input
                  type="text"
                  value={userId}
                  onChange={(event) => {
                    setUserId(event.target.value);
                    if (event.target.value !== selectedUser?._id) {
                      setSelectedUser(null);
                    }
                  }}
                  placeholder="Select a customer above"
                  maxLength={24}
                  autoComplete="off"
                  style={styles.input}
                  disabled={submitting}
                />
              </label>

              <div style={styles.field}>
                <span style={styles.label}>Transaction Type</span>
                <div style={styles.typeOptions}>
                  <button
                    type="button"
                    onClick={() => setType("credit")}
                    disabled={submitting}
                    style={{
                      ...styles.typeButton,
                      ...(type === "credit"
                        ? styles.creditButtonActive
                        : {}),
                    }}
                  >
                    + Credit
                  </button>
                  <button
                    type="button"
                    onClick={() => setType("debit")}
                    disabled={submitting}
                    style={{
                      ...styles.typeButton,
                      ...(type === "debit"
                        ? styles.debitButtonActive
                        : {}),
                    }}
                  >
                    − Debit
                  </button>
                </div>
              </div>

              <label style={styles.field}>
                <span style={styles.label}>Amount (₹)</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="500"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  style={styles.input}
                  disabled={submitting}
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Description</span>
                <textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder={
                    type === "credit"
                      ? "Bonus cash credit by Admin"
                      : "Correction adjustment by Admin"
                  }
                  rows={4}
                  maxLength={250}
                  style={{ ...styles.input, ...styles.textarea }}
                  disabled={submitting}
                />
                <span style={styles.hint}>
                  {description.length}/250 characters
                </span>
              </label>

              <div style={styles.warning}>
                Wallet transactions affect customer funds. Check the customer,
                amount, and transaction type before confirming.
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={{
                  ...styles.submitButton,
                  ...(type === "debit" ? styles.debitSubmitButton : {}),
                  ...(submitting ? styles.disabledButton : {}),
                }}
              >
                {submitting
                  ? "Processing..."
                  : `${type === "credit" ? "Credit" : "Debit"} Wallet`}
              </button>
            </form>
          </section>
        </div>
      </main>
    </>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: "calc(100vh - 64px)",
    backgroundColor: "#f5f7fa",
    padding: "32px 20px",
  },
  pageContent: {
    maxWidth: "1100px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  card: {
    padding: "28px",
    backgroundColor: "#fff",
    borderRadius: "14px",
    boxShadow: "0 4px 18px rgba(26, 35, 50, 0.08)",
  },
  userCard: {
    padding: "24px",
    backgroundColor: "#fff",
    borderRadius: "14px",
    boxShadow: "0 4px 18px rgba(26, 35, 50, 0.08)",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    marginBottom: "20px",
  },
  title: {
    margin: 0,
    color: "#1a2332",
    fontSize: "26px",
  },
  subtitle: {
    margin: "7px 0 0",
    color: "#667085",
    fontSize: "14px",
    lineHeight: 1.5,
  },
  totalBadge: {
    padding: "7px 12px",
    borderRadius: "20px",
    color: "#594bc4",
    backgroundColor: "#eeecff",
    fontSize: "13px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  searchRow: {
    display: "flex",
    gap: "9px",
    marginBottom: "18px",
    flexWrap: "wrap",
  },
  searchButton: {
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#6c5ce7",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  clearButton: {
    padding: "10px 18px",
    border: "1px solid #d0d5dd",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#475467",
    fontWeight: 600,
    cursor: "pointer",
  },
  tableWrapper: {
    overflowX: "auto",
    border: "1px solid #e4e7ec",
    borderRadius: "10px",
  },
  table: {
    width: "100%",
    minWidth: "700px",
    borderCollapse: "collapse",
  },
  tableHeadRow: {
    backgroundColor: "#f8f9fb",
  },
  tableHeadCell: {
    padding: "11px 14px",
    textAlign: "left",
    color: "#667085",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  tableRow: {
    borderTop: "1px solid #e4e7ec",
    cursor: "pointer",
  },
  selectedRow: {
    backgroundColor: "#f1efff",
  },
  tableCell: {
    padding: "12px 14px",
    color: "#344054",
    fontSize: "14px",
  },
  emptyCell: {
    padding: "30px",
    color: "#667085",
    textAlign: "center",
  },
  customerName: {
    color: "#1d2939",
    fontWeight: 700,
  },
  customerEmail: {
    marginTop: "3px",
    color: "#667085",
    fontSize: "12px",
  },
  walletAmount: {
    color: "#087443",
  },
  selectButton: {
    padding: "7px 13px",
    border: "1px solid #6c5ce7",
    borderRadius: "7px",
    color: "#6c5ce7",
    backgroundColor: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  selectedButton: {
    color: "#fff",
    backgroundColor: "#6c5ce7",
  },
  pagination: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "14px",
  },
  pageButton: {
    padding: "8px 14px",
    border: "1px solid #d0d5dd",
    borderRadius: "7px",
    color: "#344054",
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  pageLabel: {
    color: "#667085",
    fontSize: "13px",
  },
  selectedCustomer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    marginBottom: "20px",
    padding: "13px 15px",
    border: "1px solid #c7bfff",
    borderRadius: "9px",
    color: "#344054",
    backgroundColor: "#f6f5ff",
  },
  selectedCustomerDetail: {
    marginTop: "3px",
    color: "#667085",
    fontSize: "12px",
  },
  selectedBalance: {
    color: "#087443",
    fontSize: "14px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  typeBadge: {
    padding: "7px 14px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: 700,
  },
  creditBadge: {
    color: "#087443",
    backgroundColor: "#e9f9f0",
  },
  debitBadge: {
    color: "#b42318",
    backgroundColor: "#fff0ee",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "7px",
  },
  label: {
    color: "#344054",
    fontSize: "14px",
    fontWeight: 600,
  },
  input: {
    flex: 1,
    minWidth: "220px",
    boxSizing: "border-box",
    padding: "11px 13px",
    border: "1px solid #d0d5dd",
    borderRadius: "8px",
    fontSize: "15px",
    outlineColor: "#6c5ce7",
    fontFamily: "inherit",
  },
  textarea: {
    width: "100%",
    resize: "vertical",
  },
  hint: {
    color: "#7a8494",
    fontSize: "12px",
  },
  typeOptions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
  },
  typeButton: {
    padding: "12px",
    border: "1px solid #d0d5dd",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#475467",
    fontSize: "15px",
    fontWeight: 700,
    cursor: "pointer",
  },
  creditButtonActive: {
    color: "#087443",
    borderColor: "#12b76a",
    backgroundColor: "#ecfdf3",
  },
  debitButtonActive: {
    color: "#b42318",
    borderColor: "#f04438",
    backgroundColor: "#fff0ee",
  },
  warning: {
    padding: "12px 14px",
    color: "#934900",
    backgroundColor: "#fff8e7",
    border: "1px solid #fedf89",
    borderRadius: "8px",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  submitButton: {
    padding: "13px 18px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#12a05c",
    color: "#fff",
    fontSize: "15px",
    fontWeight: 700,
    cursor: "pointer",
  },
  debitSubmitButton: {
    backgroundColor: "#d92d20",
  },
  disabledButton: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  errorAlert: {
    marginBottom: "20px",
    padding: "12px 14px",
    border: "1px solid #fda29b",
    borderRadius: "8px",
    color: "#b42318",
    backgroundColor: "#fff0ee",
    fontSize: "14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },
  retryButton: {
    border: "none",
    color: "#b42318",
    backgroundColor: "transparent",
    fontWeight: 700,
    cursor: "pointer",
  },
  successAlert: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    marginBottom: "20px",
    padding: "12px 14px",
    border: "1px solid #6ce9a6",
    borderRadius: "8px",
    color: "#087443",
    backgroundColor: "#ecfdf3",
    fontSize: "14px",
  },
};
